// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { DoubledTokens, Celebration, Icon } from "../Common.jsx";
import { useGameTelemetry } from "../../lib/metrics.js";
import { oinkSessionOP } from "../../lib/oinks.js";
import { generateAdaptiveTTSession, generateTTSession, withAnswerPrompt } from "../../lib/maths.js";
import { t as ct } from "../../lib/content.js";
import { answersMatch, playDing, playError, OinkIntroLine, TestOverlay, TokenRewardRow, OinkRewardRow } from "./shared.jsx";
import { NumericKeypad } from "./keyboards.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function TTStreaksTest({ doubleDay = false, tables, sessionSize, streakPassCount, passReward, fullReward, onLearnEarn, adaptiveState, adaptiveParamsValue, onItemResults, payoutsPerWeek, payGate, payoutsUsed, kidTheme, weeklyCapMessage, oinksOn, onEarnOinks, partyEmoji, onSessionFinished }) {
  const ttResultsRef = useRef([]);
  const ttHistoryRef = useRef([]); // LB-366: what was typed, for the test history
  const [sessionKey, setSessionKey] = useState(0);
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase] = useState("intro");
  // W2.5 Learn & Earn funnel: game_start / game_complete / game_abandon ride the
  // shared phase shape (intro → testing → finished). Age band is stamped by the
  // metrics module (App.jsx keeps it current), so no child prop is needed here.
  useGameTelemetry("ttstreaks", phase);
  const [awarded, setAwarded] = useState(false);
  // Set when onLearnEarn routes this pass's reward to the parent instead of crediting it
  // instantly (spending cap breached/blocked) — holds the kid-friendly message to show in
  // place of the usual reward celebration.
  const [capNotice, setCapNotice] = useState(null);
  const [showBurst, setShowBurst] = useState(false);
  // True specifically when a miss ended the run early because too few questions were left
  // to still form a scoring streak (see remainingAfterThis in check() below) — drives the
  // "Streak over!" framing on the results screen. A run that plays all the way to the end
  // (surviving misses along the way, or none at all) is NOT this case even if it never
  // reached the pass threshold — that's just a normal completion with a low best streak,
  // and gets the gentler "try again" framing instead ("So close" only one answer short).
  const [endedByMiss, setEndedByMiss] = useState(false);
  const advanceRef = useRef(null);
  const burstRef = useRef(null);

  const perWeek = payoutsPerWeek || 1;
  // 12 Sep (Alan): on Coach and By-Year lists a game pays once at the PASS tier and once at
  // FULL marks, so scoring 80% no longer ends the week — going back for 100% still pays,
  // which is the behaviour worth encouraging. Custom lists keep the plain weekly count,
  // because replaying a parent's own short word list IS farmable. payGate falls back to the
  // old rule when a caller has not supplied one.
  const gate = payGate || { full: (payoutsUsed || 0) < perWeek, pass: (payoutsUsed || 0) < perWeek };
  const onCooldown = !gate.full && !gate.pass;
  // Snapshot of onCooldown taken the moment this test finishes, then frozen. Without this,
  // the results screen has a real bug: crediting THIS session's reward updates payoutsUsed
  // via the parent's state, which re-renders this component with onCooldown now true —
  // since the very payout being reported is what just pushed the weekly count to its limit
  // — so the "you earned it" card flips to "you're already on cooldown" the instant the
  // reward that just earned it lands (staging feedback, Aug 2026). Freezing the value the
  // moment phase becomes "finished" keeps the results screen honest about the session it's
  // actually reporting on, rather than about state that session's own payout just changed.
  const onCooldownAtFinishRef = useRef(onCooldown);
  if (phase !== "finished") onCooldownAtFinishRef.current = onCooldown;
  // Drawn once per run, in state, for the same reason as SpellingTest and VocabTest: this
  // was useMemo(..., [tables, sessionSize, sessionKey]), and `tables` is read straight off
  // household state, so it takes a new reference whenever that state is replaced — which
  // happens on any save and on the re-sync after the app is backgrounded. The questions
  // would regenerate underneath the child mid-run, so an answer typed for one question got
  // checked against a different one. Less exploitable than the spelling version (the score
  // is a count, not a set), but wrong in the same way and fixed the same way.
  const [session, setSession] = useState([]);
  const question = session[idx];
  // How many questions were actually seen this run — equals sessionSize on a perfect
  // completion, but a run that ends early on a miss only ever reaches idx + 1.
  const questionsSeen = idx + 1;

  useEffect(() => {
    if (phase === "finished" && !awarded) {
      setAwarded(true);
      // Oinks first: paid on every completed run regardless of cooldown, cap, or streak —
      // "perfect" here means a maxed-out streak (every question in the run correct in a row).
      const sessionOinks = oinksOn && onEarnOinks ? oinkSessionOP(correctCount, bestStreak >= sessionSize).op : 0;
      if (oinksOn && onEarnOinks) onEarnOinks(sessionOinks);
      if (onSessionFinished) onSessionFinished("ttstreaks", correctCount, questionsSeen, bestStreak >= sessionSize);
      const reward = bestStreak >= sessionSize ? fullReward : bestStreak >= streakPassCount ? passReward : 0;
      const tier = bestStreak >= sessionSize ? "full" : "pass";
      const history = { questions: ttHistoryRef.current.slice(), oinks: sessionOinks, qualified: reward > 0 ? tier : null, gateOpen: reward > 0 && !!gate[tier], passMark: streakPassCount, passKind: "streak", streak: bestStreak };
      if (onItemResults) onItemResults("ttstreaks", ttResultsRef.current.slice(), false, { total: ttHistoryRef.current.length, history });
      if (reward > 0 && gate[tier]) {
        const result = onLearnEarn(reward, `TT Streaks: best streak ${bestStreak}/${sessionSize} (${correctCount}/${questionsSeen} correct)`, "ttstreaks", tier);
        if (result && !result.credited) setCapNotice(result.capMessage || null);
      }
    }
  }, [phase]);

  useEffect(() => () => { if (advanceRef.current) clearTimeout(advanceRef.current); if (burstRef.current) clearTimeout(burstRef.current); }, []);

  if (!tables || tables.length === 0) return <div className="bg-white rounded-2xl p-6 text-center text-base text-slate-400 shadow-sm border border-slate-100">{ct("learnearn.tt.none_on", "No times tables switched on yet — ask a parent to pick some in Learn & Earn.")}</div>;

  function startTest() { ttResultsRef.current = []; ttHistoryRef.current = []; setSession(adaptiveState ? generateAdaptiveTTSession(tables, sessionSize, adaptiveState, adaptiveParamsValue) : generateTTSession(tables, sessionSize)); setSessionKey((k) => k + 1); setIdx(0); setInput(""); setResult(null); setStreak(0); setBestStreak(0); setCorrectCount(0); setAwarded(false); setCapNotice(null); setShowBurst(false); setEndedByMiss(false); setPhase("testing"); }
  function restart() { setPhase("intro"); }
  // Submitting an answer shows the result, plays a sound, and auto-advances shortly after
  // rather than waiting on a "Next" tap, so a fast-answering kid never has a dead moment
  // between questions. A wrong answer always breaks the streak, but only ends the run
  // early if there are no longer enough questions left to form a fresh streak worth a
  // reward — see the remainingAfterThis check below.
  // 12 Sep: blank answers cannot be submitted; Skip counts as a wrong answer (and so breaks
  // the streak, exactly as a wrong answer does) but lets the child carry on.
  function check(skipped = false) {
    if (result !== null) return;
    if (!skipped && !String(input).trim()) return;
    const correct = !skipped && answersMatch(input, question.answer);
    ttResultsRef.current.push({ id: question.prompt, grade: question.grade, topic: question.topic, level: question.level, correct });
    ttHistoryRef.current.push({ q: question.prompt, given: skipped ? "" : String(input).trim(), answer: question.answer, correct, skipped: !!skipped });
    setResult(correct ? "correct" : "wrong");
    if (correct) {
      playDing();
      setCorrectCount((c) => c + 1);
      const newStreak = streak + 1;
      setStreak(newStreak);
      setBestStreak((b) => Math.max(b, newStreak));
      // Celebrate the moment the target is first reached this run — not every correct answer
      // after it, and not again if it's re-reached after a later miss resets the streak.
      if (newStreak === streakPassCount) {
        setShowBurst(true);
        burstRef.current = setTimeout(() => setShowBurst(false), 1300);
      }
      advanceRef.current = setTimeout(() => { advance(); }, 700);
    } else {
      playError();
      setStreak(0);
      // A miss only ends the run early if there's no way left to form a fresh streak long
      // enough to earn a reward — with fewer questions remaining than streakPassCount
      // needs, playing on can't possibly score. Otherwise there's still a genuine shot at
      // it, so the run continues exactly as a correct answer would (advance() resets the
      // input and moves to the next question) — just with the streak back at zero.
      const remainingAfterThis = sessionSize - (idx + 1);
      if (remainingAfterThis < streakPassCount) {
        setEndedByMiss(true);
        advanceRef.current = setTimeout(() => { setPhase("finished"); }, 700);
      } else {
        advanceRef.current = setTimeout(() => { advance(); }, 700);
      }
    }
  }
  function advance() {
    setIdx((i) => {
      if (i + 1 >= sessionSize) { setPhase("finished"); return i; }
      setInput(""); setResult(null);
      return i + 1;
    });
  }

  if (phase === "intro") {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
        <div className="mb-2 flex justify-center"><Icon id="ic-times-tables" size={36} hue="indigo" /></div>
        <div className="font-semibold text-slate-700 text-sm mb-1">{sessionSize} questions from the {tables.join(", ")} times tables</div>
        <p className="text-base text-slate-500 mb-4">
          {weeklyCapMessage
            ? weeklyCapMessage
            : onCooldown
            ? `Practice only — you've already earned ${perWeek > 1 ? `all ${perWeek} rewards` : "this week's reward"} for maths, resets Monday.`
            : doubleDay
            ? <>Go the whole way to earn <DoubledTokens plus={false} base={fullReward} /> tokens, or a streak of {streakPassCount} for <DoubledTokens plus={false} base={passReward} />. <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("tests.double_token_chip", "Double token day")}</span></>
            : `Go the whole way to earn ${fullReward} tokens, or a streak of ${streakPassCount} for ${passReward}.`}
        </p>
        {oinksOn && <OinkIntroLine moneyPaused={!!weeklyCapMessage || onCooldown} />}
        <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={startTest}>{ct("tests.start_button_short", "Start")}</button>
      </div>
    );
  }

  // The flag sits at the configured streak target's proportion of the session length —
  // never a fixed cosmetic position — so a stricter or looser target visibly moves it. Fill
  // continues past the flag toward 100% once the target's cleared, since the session length
  // is a hard ceiling on how long a streak can ever get (never more correct answers than
  // there are questions), so there's no need to rescale or extend the bar itself.
  const flagPct = Math.min(96, Math.max(4, (streakPassCount / sessionSize) * 100));
  const fillPct = Math.min(100, (streak / sessionSize) * 100);
  const pastTarget = streak >= streakPassCount;

  return (
    <TestOverlay onClose={restart} focused={phase === "testing"}>
      {phase === "finished" ? (() => {
        const full = bestStreak >= sessionSize;
        const reward = full ? fullReward : bestStreak >= streakPassCount ? passReward : 0;
        const rewardEarned = reward > 0 && !onCooldownAtFinishRef.current && !capNotice;
        // A miss that never reached the pass threshold is a plain "streak over" moment —
        // give it its own punchier, kid-friendly framing rather than the same neutral
        // "best streak N" headline used for a successful or partially-successful run.
        const streakBroken = endedByMiss && reward === 0;
        const oink = oinkSessionOP(correctCount, bestStreak >= sessionSize);
        return (
          <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
            {/* Was gated on full (a maxed-out streak) — a pass-threshold streak reward earns
                tokens too, and the 🎉 messaging right below already celebrates it, so the
                confetti should match rather than only firing on the rarer maxed-out case. */}
            {rewardEarned && <Celebration variant="pig" emojiOverride={partyEmoji} />}
            <div data-testid="tt-streak-headline" className={`font-extrabold text-3xl mb-1 ${rewardEarned ? "text-emerald-500" : streakBroken ? "text-rose-500" : "text-slate-700"}`}>
              {streakBroken ? "Streak over" : `Best streak ${bestStreak}`}
            </div>
            <div className="text-sm text-slate-400 mb-2">{streakBroken ? `best streak ${bestStreak} · ` : ""}{correctCount}/{questionsSeen} correct</div>
            {capNotice ? (
              <div className="text-amber-600 font-bold text-base mb-4">{capNotice}</div>
            ) : rewardEarned ? (
              <TokenRewardRow amount={reward} detail={`best streak: ${bestStreak}${streakBroken ? "" : " — nailed it"}`} />
            ) : reward > 0 ? (
              <div className="text-slate-500 text-base mb-4">Nice streak. You've already earned {perWeek > 1 ? "all this week's maths rewards" : "this week's maths reward"} — resets Monday.</div>
            ) : (
              // Same rule as spelling: "So close" only one right answer short of paying.
              <div className="text-slate-500 text-base mb-4" data-testid="tt-miss-line">{bestStreak + 1 >= (passReward > 0 ? streakPassCount : sessionSize)
                ? ct("tests.tt.close", "So close. Try again and beat that streak.")
                : ct("tests.tt.low", "Try again and beat that streak.")}</div>
            )}
            {oinksOn && <OinkRewardRow op={oink.op} detail={oink.detail} />}
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm`} onClick={restart}>{reward > 0 ? "Back to Learn & Earn" : "Try again"}</button>
          </div>
        );
      })() : (
        <div key={`q-${sessionKey}-${idx}`} className="bg-white rounded-2xl px-3 py-5 shadow-sm border border-slate-100 text-center">
          <style>{"@keyframes ttFireFall{from{transform:translateY(-8px);opacity:1}to{transform:translateY(44px);opacity:0}}"}</style>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-slate-400 font-medium text-left">Q{idx + 1}/{session.length}</div>
            <button className="lb-inline-quit text-xs text-slate-400 font-semibold flex-shrink-0 ml-2" onClick={restart}>{ct("tests.quit_restart", "Quit / Restart")}</button>
          </div>
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-400 mb-1.5 text-left">streak {streak} of {streakPassCount}{pastTarget ? " · new best zone." : ""}</div>
            <div className="relative h-2.5 bg-slate-100 rounded-full">
              <div className={`absolute left-0 top-0 bottom-0 rounded-full ${pastTarget ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${fillPct}%`, transition: "width 300ms ease-out" }} />
              <div className="absolute" style={{ left: `${flagPct}%`, top: "-14px", transform: "translateX(-50%)" }}><Icon id="ic-goals" size={16} hue="indigo" /></div>
              <div className="absolute" style={{ left: `${fillPct}%`, top: "-9px", transform: "translateX(-50%)", transition: "left 300ms ease-out" }}><Icon id="ic-flame" size={18} hue="amber" /></div>
            </div>
            {showBurst && (
              <div className="relative h-9 overflow-hidden" aria-hidden="true">
                {Array.from({ length: 9 }).map((_, i) => (
                  <span key={i} className="absolute text-lg" style={{ left: `${(i * 41) % 100}%`, animation: `ttFireFall ${0.7 + (i % 3) * 0.15}s ease-in ${i * 55}ms` }}><Icon id="ic-flame" size={18} hue="amber" /></span>
                ))}
              </div>
            )}
          </div>
          <div data-testid="tt-question" className="font-extrabold text-3xl text-slate-800 mb-4">{withAnswerPrompt(question.prompt)}</div>
          <NumericKeypad value={input} disabled={result !== null} allowDecimal={false} allowNegative={false} onEnter={() => check()}
            onDigit={(d) => setInput((v) => (v.length < 6 ? v + d : v))}
            onBackspace={() => setInput((v) => v.slice(0, -1))} />
          {result === null ? (
            <>
              <button disabled={!String(input).trim()} className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-base w-full mt-3 disabled:opacity-40`} onClick={() => check()}>{ct("tests.check_button", "Check")}</button>
              <button className="w-full mt-2 text-sm font-bold text-slate-500 py-2" data-testid="test-skip" onClick={() => check(true)}>{ct("tests.skip_question", "Skip this question")}</button>
            </>
          ) : (
            <div className={`font-bold text-base mt-3 flex items-center justify-center gap-1.5 ${result === "correct" ? "text-emerald-500" : "text-rose-500"}`}>
              <Icon id={result === "correct" ? "ic-celebrate" : "ic-close"} size={18} hue={result === "correct" ? "pink" : "rose"} />
              {result === "correct" ? "Correct" : `It was ${question.answer}`}
            </div>
          )}
        </div>
      )}
    </TestOverlay>
  );
}
